import * as THREE from 'three';
import { makeSlidingHandle } from './slidingHandle';
import profileData from './sd150-profiles.json';
import { makeRailFloorInfill } from './railFloorInfill';

function inside(point, ring) {
  let hit = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i], b = ring[j];
    if ((a[1] > point[1]) !== (b[1] > point[1]) && point[0] < (b[0] - a[0]) * (point[1] - a[1]) / (b[1] - a[1]) + a[0]) hit = !hit;
  }
  return hit;
}

export function shapesFromContours(contours) {
  const rings = contours.map(points => ({ points, area: Math.abs(THREE.ShapeUtils.area(points.map(p => new THREE.Vector2(...p)))) }));
  rings.sort((a, b) => b.area - a.area);
  rings.forEach((ring, i) => {
    ring.parent = rings.slice(0, i).reverse().find(other => inside(ring.points[0], other.points));
    ring.depth = ring.parent ? ring.parent.depth + 1 : 0;
  });
  return rings.filter(ring => ring.depth % 2 === 0).map(ring => {
    const shape = new THREE.Shape(ring.points.map(p => new THREE.Vector2(p[0] / 1000, p[1] / 1000)));
    shape.holes = rings.filter(r => r.parent === ring && r.depth % 2 === 1).map(r => new THREE.Path(r.points.map(p => new THREE.Vector2(p[0] / 1000, p[1] / 1000))));
    return shape;
  });
}

function bar(parent, dimensions, position, material) {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(...dimensions), material);
  mesh.position.set(...position); mesh.castShadow = true; mesh.receiveShadow = true; parent.add(mesh);
  return mesh;
}

export function makeRailProfile(type, length, frame, cutaway = false, library) {
  const group = new THREE.Group();
  const materials = {
    aluminium: frame,
    gasket: library.gasket,
    thermal: library.thermal,
  };
  const cutFace = library.cutAluminium;
  profileData.components.filter(c => c.category === type).forEach(component => {
    const material = materials[component.material];
    const geometry = new THREE.ExtrudeGeometry(shapesFromContours(component.contours), { depth: length, steps: 1, bevelEnabled: false, curveSegments: 3, material: cutaway && component.material === 'aluminium' ? 1 : 0, extrudeMaterial: 0 });
    geometry.translate(0, 0, -length / 2); geometry.rotateY(Math.PI / 2);
    // Three.js assigns material 0 to end faces and material 1 to the extrusion
    // walls. All long rail surfaces must share the selected frame finish.
    const mesh = new THREE.Mesh(geometry, [cutaway && component.material === 'aluminium' ? cutFace : material, material]);
    mesh.castShadow = true; mesh.receiveShadow = true; mesh.userData.cadLayer = component.layer;
    group.add(mesh);
  });
  if (type === 'sill') {
    // 10 mm lower subframe follows the PDF section; primary 40 mm CAD contour
    // above it gives the nominal 50 mm high-track assembly. This simple subframe
    // is not represented as an exact CAD extrusion in the provenance text.
    group.children.forEach(child => { child.position.y += 0.01; });
    bar(group, [length, 0.0018, 0.15], [0, 0.0009, 0], frame);
    for (const z of [-0.074, 0.074, 0]) bar(group, [length, 0.009, 0.0018], [0, 0.0055, z], frame);
    // Stainless running strips are a separate item (PDF component 20).
    // These caps follow the crown of the extracted dual-track support profiles.
    const stainless = library.steel;
    for (const z of [-0.03925, 0.03925]) {
      bar(group, [length, 0.0007, 0.021], [0, 0.0271, z], stainless);
      for (const dz of [-0.0104, 0.0104]) bar(group, [length, 0.0022, 0.0005], [0, 0.0261, z + dz], stainless);
    }
  }
  return group;
}

export function makeSD150Panel(pw, h, frame, glass, metal, materials, handle = false) {
  const panel = new THREE.Group();
  const sashBottom = makeRailProfile('sash', pw, frame, false, materials); sashBottom.position.y = .01; panel.add(sashBottom);
  for (const x of [-pw / 2 + .017, pw / 2 - .017]) bar(panel, [.034, h - .087, .0585], [x, (h + .047) / 2, 0], frame);
  bar(panel, [pw, .024, .0585], [0, h - .033, 0], frame);
  const gh = h - .142;
  for (const z of [-.011, .011]) bar(panel, [pw - .068, gh, .006], [0, .097 + gh / 2, z], glass);
  if (handle) {
    panel.add(makeSlidingHandle({ system: 'SD150', panelWidth: pw, panelHeight: h, panelIndex: 1 }, { steel: metal, gasket: materials.gasket }));
  }
  return panel;
}

export function buildSD150(parent, p, frame, glass, metal, materials) {
  const w = p.width, h = p.height, pw = (w - 0.1) / 2 + 0.018;
  const sill = makeRailProfile('sill', w, frame, false, materials); parent.add(sill);
  const head = makeRailProfile('head', w, frame, false, materials); head.position.y = h; parent.add(head);
  // Jambs retain two receiver channels instead of a filled rectangular block.
  for (const x of [-w / 2 + 0.022, w / 2 - 0.022]) {
    bar(parent, [0.044, h, 0.002], [x, h / 2, -0.074], frame);
    bar(parent, [0.044, h, 0.002], [x, h / 2, 0.074], frame);
    for (const z of [-0.074, 0, 0.074]) bar(parent, [0.003, h, 0.003], [x + (x < 0 ? 0.02 : -0.02), h / 2, z], frame);
    bar(parent, [0.003, h, 0.15], [x + (x < 0 ? -0.02 : 0.02), h / 2, 0], frame);
  }
  const panels = [];
  for (let i = 0; i < 2; i++) {
    const panel = makeSD150Panel(pw, h, frame, glass, metal, materials, i === 1);
    panel.name = `sd150-panel-${i}`;
    panel.position.set((i === 0 ? -1 : 1) * (w - 0.1) / 4, 0, i === 0 ? -0.03925 : 0.03925);
    panels.push(panel); parent.add(panel);
  }
  const origin = panels[1].position.x;
  return value => { panels[1].position.x = origin - value * (pw - 0.055); };
}

export function makeRailSpecimen(type, frame, materials) {
  const group = new THREE.Group();
  group.userData = { productId: 'sd-150', type, trackCount: 2, profileDepth: 150 };
  const assembly = new THREE.Group(); group.add(assembly);
  const rail = makeRailProfile(type, 0.42, frame, true, materials); assembly.add(rail);
  if (type === 'sill') {
    const sash = makeRailProfile('sash', 0.13, frame, true, materials); sash.position.set(-0.14, 0.01, 0.03925); assembly.add(sash);
    const glass = materials.glass;
    for (const dz of [-0.011, 0.011]) bar(assembly, [0.13, 0.09, 0.006], [-0.14, 0.142, 0.03925 + dz], glass);
    const slabSide = materials.floorEdge;
    const floorFinish = materials.floor;
    // Cutaway floor blocks stop at the profile edges and expose its end face.
    // The floor top is y=0 in both installation modes; only the rail is recessed.
    for (const z of [-0.119, 0.119]) {
      // Substrate stops at the underside of the finish. Coplanar top faces
      // produce moire/z-fighting once distinct PBR textures are applied.
      bar(group, [0.42, 0.071, 0.086], [0, -0.0395, z], slabSide);
      bar(group, [0.42, 0.004, 0.086], [0, -0.002, z], floorFinish);
    }
    // Track centres are 78.5 mm apart and sash profiles 58.5 mm deep, leaving
    // a 20 mm swept-envelope gap. A 16 mm infill leaves 2 mm each side.
    // The clearance and support are a concept, not a manufacturer-approved detail.
    const centerInfill = bar(group, [0.42, 0.012, 0.016], [0, -0.006, 0], floorFinish);
    centerInfill.visible = false;
    const railFloorInfill = makeRailFloorInfill(0.42, floorFinish);
    group.add(railFloorInfill);
    let currentInstallation = 'raised', showCenter = true, showRailFloor = true;
    group.userData.setInstallation = installation => {
      currentInstallation = installation;
      assembly.position.y = installation === 'flush' ? -0.05 : 0;
      centerInfill.visible = installation === 'flush' && showCenter;
      railFloorInfill.visible = installation === 'flush' && showRailFloor;
    };
    group.userData.setCenterFloor = enabled => { showCenter = enabled; centerInfill.visible = enabled && currentInstallation === 'flush'; };
    group.userData.setRailFloor = enabled => { showRailFloor = enabled; railFloorInfill.visible = enabled && currentInstallation === 'flush'; };
  } else rail.position.y = 0.055;
  return group;
}